import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import ChatHtmlContent from './ChatHtmlContent';
import { AttachmentUrlResolver, downloadChatAttachment } from './ChatAttachmentPreview';
import { ChatAttachment } from './PromptChatAdapter';
import { CHAT_HTML_CONTENT_TYPE, DEFAULT_CHAT_HTML_FILE_NAME } from './ChatHtmlConstants';

export type ChatHtmlResolver = (fileId: string, signal?: AbortSignal) => Promise<string>;

export default function ChatHtmlFileContent({ fileId, fileName, scripts, resolveHtml, resolveAttachmentUrl }: {
	fileId: string; fileName?: string; scripts: boolean; resolveHtml?: ChatHtmlResolver; resolveAttachmentUrl?: AttachmentUrlResolver;
}) {
	const root = useRef<HTMLDivElement>(null);
	const [visible, setVisible] = useState(false);
	const [html, setHtml] = useState<string>();
	const [error, setError] = useState(false);
	const [downloadError, setDownloadError] = useState(false);
	const [attempt, setAttempt] = useState(0);
	useEffect(() => {
		if (!root.current || typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
		const observer = new IntersectionObserver((entries) => {
			if (entries.some((entry) => entry.isIntersecting)) { setVisible(true); observer.disconnect(); }
		}, { rootMargin: '160px' });
		observer.observe(root.current);
		return () => observer.disconnect();
	}, []);
	useEffect(() => {
		if (!visible || !resolveHtml) return;
		const abort = new AbortController();
		let active = true;
		setError(false);
		setHtml(undefined);
		console.info('[ChatHtmlFileContent HTML trace] starting preview fetch', { fileId, scripts });
		void resolveHtml(fileId, abort.signal).then((value) => {
			console.info('[ChatHtmlFileContent HTML trace] preview fetch succeeded', { fileId, characterCount: value.length, active });
			if (active) setHtml(value);
		}).catch((error: unknown) => {
			if (abort.signal.aborted) {
				console.info('[ChatHtmlFileContent HTML trace] preview fetch aborted', { fileId });
				return;
			}
			console.warn('[ChatHtmlFileContent HTML trace] preview fetch failed', {
				fileId,
				errorName: error instanceof Error ? error.name : typeof error,
				errorMessage: error instanceof Error ? error.message : undefined,
			});
			if (active) setError(true);
		});
		return () => { active = false; abort.abort(); };
	}, [attempt, fileId, resolveHtml, scripts, visible]);
	useEffect(() => {
		if (!resolveHtml) {
			console.warn('[ChatHtmlFileContent HTML trace] preview resolver missing; no fetch will be attempted', { fileId, scripts });
		} else if (!visible) {
			console.info('[ChatHtmlFileContent HTML trace] waiting for attachment to become visible', { fileId });
		}
	}, [fileId, resolveHtml, scripts, visible]);
	const downloadAttachment = async () => {
		if (!resolveAttachmentUrl) return;
		setDownloadError(false);
		try {
			const attachment: ChatAttachment = { fileId, fileName: fileName ?? DEFAULT_CHAT_HTML_FILE_NAME, contentType: CHAT_HTML_CONTENT_TYPE };
			const url = await resolveAttachmentUrl(attachment);
			downloadChatAttachment(url, attachment.fileName);
		} catch {
			setDownloadError(true);
		}
	};
	return <Box ref={root} sx={{ mt: 1, minHeight: 80 }}>
		{!resolveHtml ? <Alert severity="info">HTML preview is unavailable.</Alert>
			: error ? <Alert severity="error" action={<Button onClick={() => setAttempt((value) => value + 1)}>Retry</Button>}>The HTML preview could not be loaded.</Alert>
				: html === undefined ? <CircularProgress size={20} />
					: <ChatHtmlContent html={html} scripts={scripts} fileName={fileName} onDownload={resolveAttachmentUrl ? downloadAttachment : undefined} />}
		{downloadError && <Alert severity="error" sx={{ mt: 0.5 }}>The HTML attachment could not be downloaded.</Alert>}
	</Box>;
}
